import { useEffect, useRef, useState } from 'react';
import c from '../../../utils/tailwind-merge';
import { IconButton } from '../../atoms/Button/IconButton';

type GalleryControlsProps = {
  currentSlide: number;
  totalSlides: number;
  nextSlide: () => void;
  previousSlide: () => void;
  previousAriaLabel: string;
  nextAriaLabel: string;
};

export const GalleryControls = ({
  currentSlide,
  totalSlides,
  nextSlide,
  previousSlide,
  previousAriaLabel,
  nextAriaLabel,
}: GalleryControlsProps) => {
  const buttonClassNames = `h-9 w-9 rounded-full`;
  const iconClassNames = `h-6 w-6 fill-content-subtle hover:fill-grey-800`;

  const [liveMessage, setLiveMessage] = useState('');
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    setLiveMessage(`Slide ${currentSlide + 1} of ${totalSlides}`);
  }, [currentSlide, totalSlides]);

  return (
    <div className="w-full bg-surface-muted px-2 py-2 md:rounded-b-lg lg:px-0 lg:py-0">
      <div className="mx-auto flex w-full items-center justify-between lg:w-fit">
        <IconButton
          iconName="CaretLeft"
          ariaLabel={previousAriaLabel}
          className={buttonClassNames}
          iconClassName={c(iconClassNames)}
          iconSize={24}
          testId="gallery-previous-button"
          onClick={previousSlide}
        />
        <span className="tiny-text mx-2 mt-px font-bold text-content-subtle">
          {currentSlide + 1} / {totalSlides}
        </span>
        {/* Live region for screen readers: present on load (initially empty) and updated when slide changes */}
        <div role="status" aria-atomic="true" className="sr-only">
          {liveMessage}
        </div>
        <IconButton
          iconName="CaretRight"
          ariaLabel={nextAriaLabel}
          className={buttonClassNames}
          iconClassName={c(iconClassNames)}
          iconSize={24}
          onClick={nextSlide}
          testId="gallery-next-button"
        />
      </div>
    </div>
  );
};
